// The tool-window layout (spec 6.8): a pure model, its reducer, the effective layout for the current page and
// screen, and the geometry rules. Rendering lives in Workspace.tsx.

export type DockZone = 'left' | 'right' | 'bottom';
export const ZONES: readonly DockZone[] = ['left', 'right', 'bottom'];

/** Where a visible window shows: docked in its zone, or floating at its rectangle. */
export type WindowMode = 'docked' | 'floating';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface WindowLayout {
  visible: boolean;
  mode: WindowMode;
  /** The zone it is docked in, or returns to when docked again. */
  zone: DockZone;
  /** Last floating rectangle, relative to the workspace; null = the default rectangle. */
  rect: Rect | null;
}

export interface ZoneLayout {
  /** Height (bottom) or width (left, right) in px, as chosen; drawn clamped (zoneSizes). */
  size: number;
  /** Every window whose zone this is, in tab order (also hidden and floating ones). */
  tabs: string[];
  active: string | null;
  collapsed: boolean;
}

/** The persisted layout (spec 6.8.8). */
export interface DockLayout {
  windows: Record<string, WindowLayout>;
  zones: Record<DockZone, ZoneLayout>;
  /** Floating windows' stacking order, last on top. */
  floating: string[];
  /** Each window's view settings, by window id. */
  views: Record<string, unknown>;
}

/** The layout plus what isn't persisted. */
export interface DockState {
  layout: DockLayout;
  maximized: string | null;
}

/** A window's place in the default layout. */
export interface WindowDefaults {
  id: string;
  zone: DockZone;
  visible: boolean;
  /** The active tab of its zone by default (else the first visible one is). */
  active?: boolean;
}

export const GEOMETRY = {
  bottom: { size: 340, min: 160, maxFraction: 0.7 },
  side: { size: 430, min: 320, maxFraction: 0.6 },
  floating: { width: 900, height: 430, minWidth: 420, minHeight: 240 },
  page: { minWidth: 480, minHeight: 240 },
  /** Part of a floating window's title bar that always stays inside the workspace. */
  titleVisible: 48,
  /** Height of a zone header and of a floating title bar. */
  header: 34,
  /** Width of a collapsed side zone. */
  collapsedSide: 36,
  /** Below this viewport width, side and floating windows show in the bottom zone. */
  narrow: 1024,
  /** Splitter steps (spec 6.8.6). */
  step: 16,
  bigStep: 64,
} as const;

export function isSide(zone: DockZone): boolean {
  return zone !== 'bottom';
}

export function defaultSize(zone: DockZone): number {
  return isSide(zone) ? GEOMETRY.side.size : GEOMETRY.bottom.size;
}

export function minSize(zone: DockZone): number {
  return isSide(zone) ? GEOMETRY.side.min : GEOMETRY.bottom.min;
}

export function defaultLayout(windows: readonly WindowDefaults[]): DockLayout {
  const zones = Object.fromEntries(
    ZONES.map((zone) => {
      const inZone = windows.filter((w) => w.zone === zone);
      const visible = inZone.filter((w) => w.visible);
      const active = visible.find((w) => w.active) ?? visible[0];
      return [
        zone,
        {
          size: defaultSize(zone),
          tabs: inZone.map((w) => w.id),
          active: active?.id ?? null,
          collapsed: false,
        },
      ];
    }),
  ) as Record<DockZone, ZoneLayout>;
  return {
    windows: Object.fromEntries(
      windows.map((w) => [w.id, { visible: w.visible, mode: 'docked', zone: w.zone, rect: null }]),
    ),
    zones,
    floating: [],
    views: {},
  };
}

export type DockAction =
  | { type: 'show'; id: string }
  | { type: 'hide'; id: string }
  /** `index` is the position among the zone's other tabs; without it a window already there keeps its place. */
  | { type: 'dock'; id: string; zone: DockZone; index?: number }
  /** Without `rect` the window floats at its last floating rectangle. */
  | { type: 'float'; id: string; rect?: Rect | null }
  | { type: 'maximize'; id: string }
  | { type: 'restore' }
  | { type: 'activate'; id: string }
  | { type: 'resize'; zone: DockZone; size: number }
  | { type: 'collapse'; zone: DockZone; collapsed: boolean }
  | { type: 'move'; id: string; rect: Rect }
  | { type: 'reset-window'; id: string }
  | { type: 'reset' }
  | { type: 'dock-all' }
  | { type: 'hide-all' }
  | { type: 'view'; id: string; view: unknown };

export interface DockContext {
  defaults: DockLayout;
  /** Below 1024 px: side and floating windows show in the bottom zone (spec 6.8.9). */
  narrow: boolean;
}

/** The zone a window shows in: its own, or the bottom one on narrow screens. */
export function displayZone(window: WindowLayout, narrow: boolean): DockZone | null {
  if (window.mode === 'floating') return narrow ? 'bottom' : null;
  return narrow && isSide(window.zone) ? 'bottom' : window.zone;
}

function withWindow(layout: DockLayout, id: string, patch: Partial<WindowLayout>): DockLayout {
  const current = layout.windows[id];
  if (!current) return layout;
  return { ...layout, windows: { ...layout.windows, [id]: { ...current, ...patch } } };
}

function withZone(layout: DockLayout, zone: DockZone, patch: Partial<ZoneLayout>): DockLayout {
  return { ...layout, zones: { ...layout.zones, [zone]: { ...layout.zones[zone], ...patch } } };
}

function toFront(layout: DockLayout, id: string): DockLayout {
  return { ...layout, floating: [...layout.floating.filter((w) => w !== id), id] };
}

function withoutFloating(layout: DockLayout, id: string): DockLayout {
  return layout.floating.includes(id)
    ? { ...layout, floating: layout.floating.filter((w) => w !== id) }
    : layout;
}

/** The zone's active tab moves to a neighbour that is still docked and visible there. */
function leaveActive(layout: DockLayout, zone: DockZone, id: string): DockLayout {
  const z = layout.zones[zone];
  if (z.active !== id) return layout;
  const candidates = z.tabs.filter((t) => {
    const w = layout.windows[t];
    return t !== id && w?.visible && w.mode === 'docked' && w.zone === zone;
  });
  const index = z.tabs.indexOf(id);
  const next = candidates.find((t) => z.tabs.indexOf(t) > index) ?? candidates[candidates.length - 1] ?? null;
  return withZone(layout, zone, { active: next });
}

/** Makes `id` the active, expanded tab of the zone it shows in (and of its own zone). */
function activateDocked(layout: DockLayout, id: string, narrow: boolean): DockLayout {
  const window = layout.windows[id];
  if (!window) return layout;
  let next = withZone(layout, window.zone, { active: id, collapsed: false });
  const shown = displayZone(window, narrow);
  if (shown && shown !== window.zone) next = withZone(next, shown, { active: id, collapsed: false });
  return next;
}

/**
 * A drop position among a strip's displayed tabs as a position in the zone's stored tabs, which also hold hidden
 * and unavailable windows (code review C12): before the displayed tab at `index`, or right after the last
 * displayed one. Both lists are without the dragged window, as `placeInZone` inserts.
 */
export function storedTabIndex(
  stored: readonly string[],
  displayed: readonly string[],
  index: number,
  id: string,
): number {
  const rest = stored.filter((t) => t !== id);
  const shown = displayed.filter((t) => t !== id && rest.includes(t));
  if (index < shown.length) return rest.indexOf(shown[Math.max(0, index)]);
  const last = shown[shown.length - 1];
  return last === undefined ? rest.length : rest.indexOf(last) + 1;
}

function placeInZone(layout: DockLayout, id: string, zone: DockZone, index?: number): DockLayout {
  const window = layout.windows[id];
  if (!window) return layout;
  if (window.zone === zone && index === undefined && layout.zones[zone].tabs.includes(id)) return layout;
  const zones = { ...layout.zones };
  for (const z of ZONES) zones[z] = { ...zones[z], tabs: zones[z].tabs.filter((t) => t !== id) };
  const tabs = [...zones[zone].tabs];
  const at = index === undefined ? tabs.length : Math.max(0, Math.min(index, tabs.length));
  tabs.splice(at, 0, id);
  zones[zone] = { ...zones[zone], tabs };
  return { ...layout, zones };
}

function layoutReducer(layout: DockLayout, action: DockAction, context: DockContext): DockLayout {
  const { narrow } = context;
  switch (action.type) {
    case 'show': {
      const window = layout.windows[action.id];
      if (!window) return layout;
      const next = withWindow(layout, action.id, { visible: true });
      return window.mode === 'floating' && !narrow
        ? toFront(next, action.id)
        : activateDocked(next, action.id, narrow);
    }
    case 'hide': {
      const window = layout.windows[action.id];
      if (!window) return layout;
      let next = withWindow(layout, action.id, { visible: false });
      if (window.mode === 'docked') next = leaveActive(next, window.zone, action.id);
      return next;
    }
    case 'dock': {
      const window = layout.windows[action.id];
      if (!window) return layout;
      let next = layout;
      if (window.mode === 'docked' && window.zone !== action.zone)
        next = leaveActive(next, window.zone, action.id);
      next = placeInZone(next, action.id, action.zone, action.index);
      next = withWindow(next, action.id, { visible: true, mode: 'docked', zone: action.zone });
      next = withoutFloating(next, action.id);
      return activateDocked(next, action.id, narrow);
    }
    case 'float': {
      const window = layout.windows[action.id];
      if (!window) return layout;
      let next = window.mode === 'docked' ? leaveActive(layout, window.zone, action.id) : layout;
      next = withWindow(next, action.id, {
        visible: true,
        mode: 'floating',
        rect: action.rect !== undefined ? action.rect : window.rect,
      });
      return toFront(next, action.id);
    }
    case 'activate': {
      const window = layout.windows[action.id];
      if (!window) return layout;
      return window.mode === 'floating' && !narrow
        ? toFront(layout, action.id)
        : activateDocked(layout, action.id, narrow);
    }
    case 'resize':
      return withZone(layout, action.zone, { size: Math.max(minSize(action.zone), Math.round(action.size)) });
    case 'collapse':
      return withZone(layout, action.zone, { collapsed: action.collapsed });
    case 'move':
      return withWindow(layout, action.id, { rect: action.rect });
    case 'reset-window': {
      const window = layout.windows[action.id];
      const fallback = context.defaults.windows[action.id];
      if (!window || !fallback) return layout;
      let next = window.mode === 'docked' ? leaveActive(layout, window.zone, action.id) : layout;
      next = placeInZone(next, action.id, fallback.zone);
      next = withWindow(next, action.id, { mode: 'docked', zone: fallback.zone, rect: null });
      next = withoutFloating(next, action.id);
      return window.visible ? activateDocked(next, action.id, narrow) : next;
    }
    case 'reset':
      return context.defaults;
    case 'dock-all': {
      let next = layout;
      for (const id of layout.floating) {
        const window = next.windows[id];
        if (!window) continue;
        next = withWindow(next, id, { mode: 'docked' });
        next = placeInZone(next, id, window.zone);
        if (window.visible) next = activateDocked(next, id, narrow);
      }
      return { ...next, floating: [] };
    }
    case 'hide-all':
      return {
        ...layout,
        windows: Object.fromEntries(
          Object.entries(layout.windows).map(([id, w]) => [id, { ...w, visible: false }]),
        ),
      };
    case 'view':
      return { ...layout, views: { ...layout.views, [action.id]: action.view } };
    case 'maximize':
    case 'restore':
      return layout;
  }
}

export function dockReducer(state: DockState, action: DockAction, context: DockContext): DockState {
  const layout = layoutReducer(state.layout, action, context);
  let maximized = state.maximized;
  switch (action.type) {
    case 'maximize':
      return layout.windows[action.id]
        ? {
            layout: layout.windows[action.id].visible
              ? layout
              : withWindow(layout, action.id, { visible: true }),
            maximized: action.id,
          }
        : state;
    case 'restore':
      maximized = null;
      break;
    case 'hide':
    case 'dock':
    case 'float':
    case 'reset-window':
      if (maximized === action.id) maximized = null;
      break;
    case 'reset':
    case 'dock-all':
    case 'hide-all':
      maximized = null;
      break;
    default:
      break;
  }
  return layout === state.layout && maximized === state.maximized ? state : { layout, maximized };
}

/** What is drawn: per zone its tabs and active tab, the floating windows and the maximized one. */
export interface EffectiveLayout {
  zones: Record<DockZone, { tabs: string[]; active: string | null; collapsed: boolean }>;
  /** Bottom to top. */
  floating: string[];
  maximized: string | null;
  /** Every window that is drawn with its content: active tabs, floating and maximized windows. */
  displayed: Set<string>;
}

/**
 * Below 768 px (spec 6.8.9): no dock zones and no floating windows, only the window open as the bottom sheet.
 * The stored layout is untouched and returns on wider screens.
 */
export function phoneLayout(sheet: string | null, available: ReadonlySet<string>): EffectiveLayout {
  const shown = sheet !== null && available.has(sheet) ? sheet : null;
  const empty = () => ({ tabs: [] as string[], active: null, collapsed: false });
  return {
    zones: { left: empty(), right: empty(), bottom: empty() },
    floating: [],
    maximized: null,
    displayed: new Set(shown ? [shown] : []),
  };
}

/**
 * The layout as drawn on this page and screen (spec 6.8.2, 6.8.9): unavailable and hidden windows are left
 * out, a zone's active tab falls back to its first tab, and below 1024 px side and floating windows show in
 * the bottom zone. The maximized window is drawn only maximized.
 */
export function effectiveLayout(
  state: DockState,
  available: ReadonlySet<string>,
  narrow: boolean,
): EffectiveLayout {
  const { layout } = state;
  const shows = (id: string) => available.has(id) && layout.windows[id]?.visible === true;
  const maximized = state.maximized && shows(state.maximized) ? state.maximized : null;
  const placed = (id: string) => shows(id) && id !== maximized;

  const docked = (zone: DockZone) =>
    layout.zones[zone].tabs.filter((id) => placed(id) && layout.windows[id].mode === 'docked');
  const floatingIds = layout.floating.filter((id) => placed(id) && layout.windows[id]?.mode === 'floating');

  const tabs: Record<DockZone, string[]> = {
    left: docked('left'),
    right: docked('right'),
    bottom: docked('bottom'),
  };
  let floating = floatingIds;
  if (narrow) {
    tabs.bottom = [...tabs.bottom, ...tabs.left, ...tabs.right, ...floatingIds];
    tabs.left = [];
    tabs.right = [];
    floating = [];
  }

  const zones = Object.fromEntries(
    ZONES.map((zone) => {
      const list = tabs[zone];
      const stored = layout.zones[zone].active;
      const active = stored && list.includes(stored) ? stored : (list[0] ?? null);
      return [zone, { tabs: list, active, collapsed: layout.zones[zone].collapsed && list.length > 0 }];
    }),
  ) as EffectiveLayout['zones'];

  const displayed = new Set<string>();
  for (const zone of ZONES) {
    const active = zones[zone].active;
    if (active && !zones[zone].collapsed) displayed.add(active);
  }
  for (const id of floating) displayed.add(id);
  if (maximized) displayed.add(maximized);
  return { zones, floating, maximized, displayed };
}

/** The window's state in the words of the design: docked, floating, maximized or hidden. */
export type WindowState = 'docked' | 'floating' | 'maximized' | 'hidden';

export function windowState(state: DockState, id: string): WindowState {
  const window = state.layout.windows[id];
  if (!window || !window.visible) return 'hidden';
  if (state.maximized === id) return 'maximized';
  return window.mode;
}

// ---------- Geometry (spec 6.8.3)

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Largest size the zone may have in this workspace, keeping the page at its minimum size. */
export function maxSize(zone: DockZone, workspace: Size, otherSide = 0): number {
  if (isSide(zone)) {
    const byFraction = Math.floor(workspace.width * GEOMETRY.side.maxFraction);
    return Math.max(0, Math.min(byFraction, workspace.width - GEOMETRY.page.minWidth - otherSide));
  }
  const byFraction = Math.floor(workspace.height * GEOMETRY.bottom.maxFraction);
  return Math.max(0, Math.min(byFraction, workspace.height - GEOMETRY.page.minHeight));
}

/** A zone size as the user may set it (splitter): within its minimum and maximum. */
export function clampSize(zone: DockZone, size: number, workspace: Size, otherSide = 0): number {
  const max = maxSize(zone, workspace, otherSide);
  return Math.round(Math.max(Math.min(size, max), Math.min(minSize(zone), max)));
}

/** Drawn sizes of the zones that have tabs (0 for empty zones; collapsed zones keep only their strip). */
export function zoneSizes(
  layout: DockLayout,
  effective: EffectiveLayout,
  workspace: Size,
): Record<DockZone, number> {
  const side = (zone: DockZone) => {
    const z = effective.zones[zone];
    if (z.tabs.length === 0) return 0;
    if (z.collapsed) return GEOMETRY.collapsedSide;
    const max = Math.floor(workspace.width * GEOMETRY.side.maxFraction);
    return Math.max(Math.min(layout.zones[zone].size, max), Math.min(GEOMETRY.side.min, max));
  };
  let left = side('left');
  let right = side('right');
  const room = Math.max(0, workspace.width - GEOMETRY.page.minWidth);
  if (left + right > room) {
    // Shrink the expanded zones so the page keeps its minimum width.
    const fixed = (effective.zones.left.collapsed ? left : 0) + (effective.zones.right.collapsed ? right : 0);
    const flexible = left + right - fixed;
    const scale = flexible > 0 ? Math.max(0, room - fixed) / flexible : 0;
    if (!effective.zones.left.collapsed) left = Math.floor(left * scale);
    if (!effective.zones.right.collapsed) right = Math.floor(right * scale);
  }
  let bottom = 0;
  const b = effective.zones.bottom;
  if (b.tabs.length > 0) {
    if (b.collapsed) bottom = GEOMETRY.header;
    else {
      const max = maxSize('bottom', workspace);
      bottom = Math.max(
        GEOMETRY.header,
        Math.min(Math.max(layout.zones.bottom.size, GEOMETRY.bottom.min), max),
      );
    }
  }
  return { left, right, bottom };
}

export function defaultRect(workspace: Size): Rect {
  const width = Math.min(GEOMETRY.floating.width, workspace.width);
  const height = Math.min(GEOMETRY.floating.height, workspace.height);
  return {
    x: Math.round((workspace.width - width) / 2),
    y: Math.round((workspace.height - height) / 2),
    width,
    height,
  };
}

/** Keeps a floating window inside the workspace with at least 48 px of its title bar visible. */
export function clampRect(rect: Rect, workspace: Size): Rect {
  const width = clamp(
    rect.width,
    Math.min(GEOMETRY.floating.minWidth, workspace.width),
    Math.max(workspace.width, 1),
  );
  const height = clamp(
    rect.height,
    Math.min(GEOMETRY.floating.minHeight, workspace.height),
    Math.max(workspace.height, 1),
  );
  return {
    x: Math.round(clamp(rect.x, GEOMETRY.titleVisible - width, workspace.width - GEOMETRY.titleVisible)),
    y: Math.round(clamp(rect.y, 0, Math.max(0, workspace.height - GEOMETRY.header))),
    width: Math.round(width),
    height: Math.round(height),
  };
}
